// src/context/AuthContext.js
// Single source of truth for auth state — replaces duplicate listener in App.js
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { auth, db } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { ensureAnonUser, processGoogleUser, handleRedirectResult, consumeDeliberateSignOut } from "./auth";
import apiService from "./apiService";

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

// DEV-ONLY impersonation, set by run_local.sh --login-as. Stripped from prod builds.
const IMPERSONATE_UID = import.meta.env.DEV
  ? import.meta.env.VITE_DEV_IMPERSONATE_UID
  : null;
const IMPERSONATE_EMAIL = import.meta.env.DEV
  ? import.meta.env.VITE_DEV_IMPERSONATE_EMAIL
  : null;
// --new-user session: same X-Dev-User-ID plumbing, but a fresh free-tier uid
// (not read-only). Only changes the dev banner copy.
const IS_NEW_USER = import.meta.env.DEV
  ? import.meta.env.VITE_DEV_IMPERSONATE_NEW_USER === "true"
  : false;
// Hides the banner ONLY — impersonation itself stays on. For capturing clean screenshots and
// screen recordings of a signed-in session, where a red debug bar across the top is the one
// thing you cannot crop out (it is a fixed overlay, so cropping shifts the whole frame).
// Opt-in per run: VITE_HIDE_DEV_BANNER=1 ./run_local.sh --login-as … — never a default, because
// the banner's whole job is to stop you mistaking a debug session for a real one.
const HIDE_DEV_BANNER = import.meta.env.DEV
  ? import.meta.env.VITE_HIDE_DEV_BANNER === "1"
  : false;

// Published on the context so surfaces that gate on a Firebase capability can tell an
// impersonated session apart from a real one. IMPERSONATE_UID is already DEV-only (see
// above), so this is false in every production build regardless of what is in the env.

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  // Whether a signed-in (non-anonymous) user has been seen in this tab. Guards the anon mint
  // below — see the note there.
  const sawRealUser = useRef(false);
  // AuthProvider is mounted inside <Router> (App.jsx), so routing is available here — which
  // is where it is needed, because the redirect sign-in completes HERE and nowhere else.
  const navigate = useNavigate();
  const location = useLocation();
  const locationRef = useRef(location.pathname);
  locationRef.current = location.pathname;

  useEffect(() => {
    let unsubscribeSnapshot = null;

    // DEV impersonation: skip the Firebase auth listener and act as the target
    // user. All API calls carry X-Dev-User-ID (see apiService.js), so the
    // backend serves this user's data.
    //
    // userData is fetched over that same header rather than left empty. It used to be
    // empty — "fine for read-only debugging" — but that made every credit-gated path
    // untestable while impersonating: the gate reads
    // `(userData?.video_limit_this_month ?? 0) - (userData?.video_spend_this_month ?? 0)`,
    // so a null userData is indistinguishable from a user with zero allowance, and
    // generating always opened the upgrade modal instead. Reading the real doc makes an
    // impersonated session behave like the account it is impersonating.
    if (IMPERSONATE_UID) {
      console.warn(
        IS_NEW_USER
          ? `🆕 DEV: new-user session as ${IMPERSONATE_EMAIL || IMPERSONATE_UID}`
          : `🕵️ DEV: impersonating ${IMPERSONATE_EMAIL || IMPERSONATE_UID}`,
      );
      apiService.setAuthReady(true);
      setUser({
        uid: IMPERSONATE_UID,
        email: IMPERSONATE_EMAIL || "impersonated@dev.local",
        isAnonymous: false,
      });
      setAuthLoading(false);
      // `--new-user` fabricates a uid and returns here, so it never reaches the sync below —
      // which is what fires the seed on a real signup. Without this the flag delivered an
      // EMPTY Workspace and called it the new-user experience, i.e. the one thing it exists
      // to show was the one thing it could not show. Safe to call on every mount: seeding is
      // idempotent on the document id. Only for --new-user; --login-as must not write to the
      // account it is debugging.
      if (IS_NEW_USER) apiService.seedStudyContent().catch(() => {});
      apiService
        .getUserProfile(IMPERSONATE_UID)
        .then((res) => {
          const doc = res?.user || res?.profile || res;
          if (doc && typeof doc === "object") setUserData(doc);
        })
        .catch((err) => console.warn("DEV impersonation: userData fetch failed", err));
      return; // no listener to unsubscribe
    }

    const init = async () => {
      // Handle redirect (Google sign-in flow) — one-time check on mount.
      //
      // Unconditional, and it must finish BEFORE the auth listener below is attached: it is
      // the call that actually resolves the credential Google sent back. Gating it on a
      // sessionStorage flag meant that whenever that flag did not survive the round trip,
      // the listener fired first, saw no user, and rendered a signed-out page — the failure
      // reported from an iPhone on 2026-09-22. Resolves to null when nothing is pending.
      try {
        const redirected = await handleRedirectResult();
        // FINISH THE JOURNEY. The popup path ends in LandingPage's own
        // `if (result?.success) navigate("/")`, but the redirect path cannot reach that line:
        // signInWithGoogle navigates away to Google before it returns, so the component that
        // would have done it is gone. Nothing then moved the visitor off /landing, and they
        // came back to a marketing page whose whole job is to show "Sign up with Google".
        //
        // Signed in, library loaded, staring at the signup button — indistinguishable from a
        // failed login, and the reason this looked broken on a phone while working on a
        // desktop. Measured 2026-09-22: /api/auth/user/sync and both library fetches returned
        // 200 under the real uid at 23:03:02, on a device whose owner was reporting that
        // logging in did not work.
        //
        // Only from the pages that exist to sign you in. A prompt raised over a shared video
        // link must leave the visitor on that video, not fling them to the home page.
        const AUTH_ENTRY_PATHS = ["/landing", "/auth", "/login"];
        if (redirected && redirected.success !== false
            && AUTH_ENTRY_PATHS.includes(locationRef.current)) {
          navigate("/", { replace: true });
        }
      } catch (err) {
        console.error("Redirect result error:", err);
      }

      // Single auth listener for the entire app
      const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
        if (unsubscribeSnapshot) {
          unsubscribeSnapshot();
          unsubscribeSnapshot = null;
        }

        apiService.setAuthReady(true);

        if (firebaseUser) {
          if (!firebaseUser.isAnonymous) sawRealUser.current = true;
          setUser(firebaseUser);
          setAuthLoading(false);

          // Sync with backend for real users (creates doc, updates lastLoginAt)
          if (!firebaseUser.isAnonymous) {
            try {
              const res = await processGoogleUser(firebaseUser);
              if (res?.user) setUserData(res.user);
            } catch (err) {
              console.error("User sync failed:", err);
            }
          }

          // Subscribe to Firestore doc for real-time updates (credits, etc.)
          const userRef = doc(db, "users", firebaseUser.uid);
          unsubscribeSnapshot = onSnapshot(
            userRef,
            (snap) => {
              if (snap.exists()) {
                setUserData(snap.data());
              }
            },
            (err) => {
              console.error("userData listener error:", err);
            },
          );
        } else {
          // DO NOT MINT AN ANONYMOUS USER OVER A REAL ONE.
          //
          // signInAnonymously() REPLACES auth.currentUser, so answering a null state with it
          // converts a recoverable blip into a permanent loss: the real session is gone and
          // the anonymous one is now what persistence holds. Measured on production
          // 2026-09-22 — signed in at 22:54:47, library loaded at 22:54:52, and at 22:55:28 a
          // brand-new anonymous uid was created and every subsequent request went out as it.
          // From the outside that is "logging in doesn't work", which is why it survived two
          // fixes aimed at the sign-in itself: the sign-in was never the broken part.
          //
          // A null state after we have seen a real user means one of two things, and only one
          // of them wants an anonymous session:
          //   they pressed Sign out  → deliberate, mint away.
          //   anything else          → a blip. Leave it alone; a restored session can still
          //                            arrive, and there is nothing to lose by waiting.
          if (sawRealUser.current && !consumeDeliberateSignOut()) {
            console.warn("⚠️ Auth went null after a real session — not minting anonymous");
            setUser(false);
            setUserData(null);
            setAuthLoading(false);
            return;
          }
          try {
            // TIMEOUT IS LOAD-BEARING. signInAnonymously can hang indefinitely — it neither
            // resolves nor rejects when the auth endpoint is unreachable. Without a bound,
            // authLoading stays true forever and the visitor sits on the splash screen with
            // no error and no way forward. Better to continue signed-out: the UI already
            // handles a null user, and the sign-in modal is one click away.
            await Promise.race([
              ensureAnonUser(),
              new Promise((_, reject) =>
                setTimeout(() => reject(new Error("anon-signin-timeout")), 8000)),
            ]);
            setUser(auth.currentUser || false);
          } catch (err) {
            console.error("Device sign-in failed:", err);
            setUser(auth.currentUser || false);
          }
          setUserData(null);
          setAuthLoading(false);
        }
      });

      return unsubscribeAuth;
    };

    let unsubscribeFn;
    init().then((fn) => {
      unsubscribeFn = fn;
    });

    return () => {
      if (unsubscribeFn) unsubscribeFn();
      if (unsubscribeSnapshot) unsubscribeSnapshot();
    };
  }, []);

  return (
    <AuthContext.Provider value={{ user, userData, authLoading, isDevImpersonating: !!IMPERSONATE_UID }}>
      {IMPERSONATE_UID && !HIDE_DEV_BANNER && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 99999,
            background: IS_NEW_USER ? "#166534" : "#b91c1c",
            color: "#ffffff",
            textAlign: "center",
            fontSize: "12px",
            fontFamily: "monospace",
            padding: "4px 8px",
            pointerEvents: "none",
          }}
        >
          {IS_NEW_USER
            ? `🆕 DEV — new user ${IMPERSONATE_EMAIL || IMPERSONATE_UID} (free tier, fresh state)`
            : `🕵️ DEV — viewing as ${IMPERSONATE_EMAIL || IMPERSONATE_UID} (read-only debug session)`}
        </div>
      )}
      {children}
    </AuthContext.Provider>
  );
};
